// CLIENT PORTAL RESTYLE — table sorting and the two client-facing row filters.
// PURE, so the rules are asserted without jsdom.

export type SortDirection = 'asc' | 'desc';

/**
 * Sort rows by one key.
 *
 * ⚠️⚠️ AN UNMEASURED VALUE SORTS LAST IN BOTH DIRECTIONS, never as a zero and
 * never as the largest. A campaign with no cost per lead (it spent and returned
 * nothing) would otherwise sort to the TOP of "cheapest first" and read as the
 * best performer on the page. Same rule the Google dashboard's attribute sort
 * follows, for the same reason.
 */
export function sortReportRows<T>(
  rows: readonly T[],
  key: Extract<keyof T, string>,
  direction: SortDirection,
): T[] {
  const dir = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = a[key] as unknown;
    const y = b[key] as unknown;
    const xMissing = x === null || x === undefined;
    const yMissing = y === null || y === undefined;
    if (xMissing && yMissing) return 0;
    // ⚠️ NOT multiplied by `dir` — "last" means last whichever way the arrow
    // points.
    if (xMissing) return 1;
    if (yMissing) return -1;
    if (typeof x === 'string' && typeof y === 'string') return dir * x.localeCompare(y);
    return dir * (Number(x) - Number(y));
  });
}

/** The Overview campaign table's platform filter. */
export type PlatformFilter = 'all' | 'meta' | 'google';

export function filterByPlatform<T extends { platform: 'meta' | 'google' }>(
  rows: readonly T[],
  filter: PlatformFilter,
): T[] {
  return filter === 'all' ? [...rows] : rows.filter((r) => r.platform === filter);
}

/**
 * The search-term filter.
 *
 * ⚠️ THREE OPTIONS, AND "NEGATIVE CANDIDATES" IS NOT ONE OF THEM. It was
 * rejected for a client-facing page: we do not label our own work a mistake in
 * a document the client keeps. "No conversions" states the same fact without
 * the verdict.
 */
export type TermFilter = 'all' | 'converting' | 'none';

export const TERM_FILTER_OPTIONS: ReadonlyArray<{ value: TermFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'converting', label: 'Converting' },
  { value: 'none', label: 'No conversions' },
];

export function filterTerms<T extends { leads: number }>(
  rows: readonly T[],
  filter: TermFilter,
): T[] {
  if (filter === 'all') return [...rows];
  if (filter === 'converting') return rows.filter((r) => r.leads > 0);
  return rows.filter((r) => r.leads === 0);
}
